
import { Options, prop, Vue } from "vue-class-component";
import { ProductCardItem } from "@/domain/entities/BookingV4";
import { formatPriceRP, formatPriceRM } from "@/app/infrastructures/misc/Utils";
import DiscountAgentBadge from "./discount-agent-badge.vue";
import { productStyle } from "./data";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  product = prop<ProductCardItem>({ required: true });
  selected = prop<boolean>({ default: false, type: Boolean });
  agentDiscountRevealed = prop<boolean>({ default: false, type: Boolean });
  isClient = prop<boolean>({ required: true, type: Boolean, default: false });
  hideDiscount = prop<boolean>({ default: false, type: Boolean });
  isNoRecommendation = prop<boolean>({ required: true, type: Boolean, default: false });
  groupName = prop<string>({
    default: "",
    type: String
  });
  // Adjust v4 per-field rules (GENESIS-20182) — threaded from product-card-list
  disableSelection = prop<boolean>({ default: false, type: Boolean });
  hidePrice = prop<boolean>({ default: false, type: Boolean });
  showAgentDiscountToggle = prop<boolean>({ required: true, type: Boolean });
  currencyPrefix = prop<string>({ required: true, type: String, default: "Rp" });
}

@Options({
  name: "ProductCardV4",
  emits: ["select"],
  components: {
    DiscountAgentBadge
  }
})
class ProductCardV4 extends Vue.with(Props) {

  get cardType(): string {
    return this.product.productType.toLowerCase();
  }

  get radioName(): string {
    return this.groupName || `product-selection-${this.product.productType}`;
  }

  get isDisabled(): boolean {
    return (
      this.disableSelection ||
      !this.product.isAvailable ||
      this.product.isEmbargo
    );
  }

  get shouldMaskAgentDiscount(): boolean {
    return !this.agentDiscountRevealed || this.isDisabled || this.hidePrice;
  }

  get isVippack(): boolean {
    return this.product.productType === "VIPPACK";
  }

  get isBosspack(): boolean {
    return this.product.productType === "BOSSPACK";
  }

  get asset() {
    return productStyle[this.product.productType];
  }

  get productLabel(): string {
    return this.asset?.label ?? this.product.productType;
  }

  get iconSrc(): string | undefined {
    if (!this.asset) return undefined;
    const image = this.isDisabled
      ? this.asset.imageInactive
      : this.asset.imageActive;
    return require(`@/app/ui/assets/svg/product-logo-v3/${image}.svg`);
  }

  get slaRange(): string {
    return `${this.product.slaMinDays}-${this.product.slaMaxDays}`;
  }

  get formattedShippingCost(): string {
    const cost = this.product.shippingCost ?? 0;
    return this.formatCurrency(cost);
  }

  get formattedCommissionAmount(): string {
    return this.formatCurrency(this.product.commissionAmount);
  }

  formatCurrency(value: number): string {
    return this.currencyPrefix === "RM"
      ? formatPriceRM(value)
      : formatPriceRP(value);
  }

  onSelect() {
    if (this.isDisabled) return;
    this.$emit("select", this.product.productType);
  }
}

export default ProductCardV4;
